import React, { useState } from "react";
import { CustomModal } from "../CustomModal";
import { Button, Form, Image, Input, Select, Upload, UploadFile, UploadProps } from "antd";
import { useTranslate } from "@obsbot/remo-i18n";
import "./index.less";
import { PlusOutlined } from "@ant-design/icons";
import { uploadFile as handleUploadFile, reportUser } from "@obsbot/remo-server-api";
import { eventBus } from "@obsbot/remo-utils";
import { FileType } from "../SetAccountModal";
export interface ReportModalProps {
    visible: boolean;
    userName: string;
    userID: string;
    setVisible: (flag: boolean) => void;
}

interface ReportFormValues {
    reason: string;
    content: string;
}
export const ReportModal: React.FC<ReportModalProps> = ({
    visible,
    setVisible,
    userName,
    userID,
}) => {
    const [form] = Form.useForm<ReportFormValues>();
    const t = useTranslate();
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [isUpload, setIsUpload] = useState<boolean>(false);
    const [evidenceUrls, setEvidenceUrls] = useState<string>("");
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState("");
    const reportOptions = [
        {
            label: t("chat.report-reason-vulgarity"),
            value: 1,
            title: "",
        },
        {
            label: t("chat.report-reason-spam"),
            value: 2,
            title: "",
        },
        {
            label: t("chat.report-reason-abuse"),
            value: 3,
            title: "",
        },
        {
            label: t("chat.report-reason-bombing"),
            value: 4,
            title: "",
        },
        {
            label: t("chat.report-reason-other"),
            value: 5,
            title: "",
        },
    ];

    const onFinish = async (values: ReportFormValues): Promise<void> => {
        console.log(values, userID);
        try {
            let filesUrls = "";
            if (fileList.length > 0 && !isUpload) {
                const formData = new FormData();
                fileList.forEach((file: any) => {
                    formData.append("files", file.originFileObj as Blob);
                });
                const res = await handleUploadFile({
                    type: "normal",
                    md5_flag: true,
                    files: formData,
                });
                if (res) {
                    filesUrls = res?.files.map(item => item.url).join(",");
                    setIsUpload(true);
                    setEvidenceUrls(filesUrls);
                }
            }
            const params = {
                reportedUserId: userID,
                reportedNickName: userName,
                reason: parseInt(values.reason),
                description: values.content,
                evidenceUrls: filesUrls || evidenceUrls,
            };
            const resSubmit = await reportUser(params);
            if (resSubmit) {
                eventBus.emit("show-message", {
                    message: t("chat.report-success"),
                    type: "success",
                });
                setVisible(false);
            }
        } catch (error) {
            console.log("举报失败", error);
        }
    };

    const getBase64 = (file: FileType): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });

    const uploadProps: UploadProps = {
        listType: "picture-card",
        accept: ".jpeg, .jpg, .png",
        fileList,
        onRemove: file => {
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            setFileList(newFileList);
        },
        beforeUpload: () => {
            return false;
        },
        onChange: ({ file, fileList }) => {
            if (!file.type?.startsWith("image/")) {
                return;
            }
            try {
                const url = URL.createObjectURL(file as unknown as File);
                fileList[fileList.length - 1].thumbUrl = url;
            } catch (error) {
                console.log("[WEB] [ERROR] [LOG] 从File对象创建Blob对象失败", error, file);
            }
            setIsUpload(false);
            setEvidenceUrls("");
            setFileList(fileList);
        },
        onPreview: async (file: UploadFile): Promise<void> => {
            if (!file.url && !file.preview) {
                file.preview = await getBase64(file.originFileObj as FileType);
            }
            setPreviewImage(file.url || (file.preview as string));
            setPreviewOpen(true);
        },
    };

    const uploadButton = (
        <button style={{ border: 0, background: "none", cursor: "pointer" }} type="button">
            <PlusOutlined onPointerMoveCapture={undefined} onPointerOverCapture={undefined} />
        </button>
    );
    return (
        <CustomModal
            className="report-modal"
            footer={null}
            open={visible}
            renderHeader={t("chat.report-user", { user: userName })}
            setVisible={setVisible}
            width={480}
            onClickClose={() => setVisible(false)}
        >
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Form.Item
                    label={t("chat.report-reason-select")}
                    name="reason"
                    rules={[{ required: true, message: t("chat.report-reason-select") }]}
                >
                    <Select
                        className="report-select"
                        optionFilterProp="label"
                        options={reportOptions}
                        rootClassName="ReportSelector"
                        onFocus={() => form.setFields([{ name: "reason", errors: [] }])}
                    />
                </Form.Item>
                <Form.Item
                    label={t("chat.report-reason-desc")}
                    name="content"
                    rules={[
                        {
                            required: true,
                            message: t("chat.report-reason-placeholder"),
                        },
                        {
                            validator: (_, value) => {
                                if (!value) {
                                    return Promise.resolve();
                                }
                                const len = value.length;
                                if (len < 5) {
                                    return Promise.reject(t("chat.report-reason-placeholder"));
                                }
                                if (len > 100) {
                                    return Promise.reject();
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Input.TextArea
                        autoSize={{ minRows: 6, maxRows: 6 }}
                        maxLength={100}
                        minLength={5}
                        placeholder={t("chat.report-reason-placeholder")}
                        showCount={true}
                        onFocus={() => form.setFields([{ name: "content", errors: [] }])}
                    />
                </Form.Item>
                <Form.Item label={t("chat.report-reason-evidence")} name="evidenceUrls">
                    <Upload {...uploadProps}>{fileList.length >= 3 ? null : uploadButton}</Upload>
                </Form.Item>
                <Form.Item className="form-footer">
                    <Button type="text" onClick={() => setVisible(false)}>
                        {t("cancel")}
                    </Button>
                    <Button htmlType="submit" type="primary">
                        {t("submit")}
                    </Button>
                </Form.Item>
            </Form>
            {previewImage && (
                <Image
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: visible => setPreviewOpen(visible),
                        afterOpenChange: visible => !visible && setPreviewImage(""),
                    }}
                    src={previewImage}
                    wrapperStyle={{ display: "none" }}
                />
            )}
        </CustomModal>
    );
};
